import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { apiGet } from '../lib/apiClient';

const RECORD_LABELS = {
  daily: 'Daily Max',
  weekly: 'Weekly Max',
  monthly: 'Monthly Max',
  all_time: 'All-time Max',
};

export default function FloatingDDRecords({ accountId = 'all', typeFilter = 'all' }) {
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const url = `/api/floating-dd/dashboard?account_id=${accountId}${typeFilter !== 'all' ? `&type_filter=${typeFilter}` : ''}`;
    apiGet(url)
      .then(setData)
      .catch(() => setData(null));
  }, [accountId, typeFilter]);

  const header = (
    <div
      className="flex items-center justify-between px-5 py-4 cursor-pointer select-none group"
      onClick={() => setCollapsed(!collapsed)}
    >
      <div className="flex items-center gap-2">
        {collapsed
          ? <ChevronRight className="w-3.5 h-3.5" style={{ color: 'var(--text-dim)' }} />
          : <ChevronDown className="w-3.5 h-3.5" style={{ color: 'var(--text-dim)' }} />}
        <AlertTriangle size={14} className="text-orange-400" />
        <h3 className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Floating Drawdown Records</h3>
        {accountId !== 'all' && (
          <span className="text-[10px] px-2 py-0.5 rounded-md" style={{
            color: 'var(--text-dim)',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-color)',
          }}>Account #{accountId}</span>
        )}
      </div>
      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${collapsed ? '-rotate-90' : ''}`} style={{ color: 'var(--text-dim)' }} />
    </div>
  );

  if (!data) return (
    <div className="rounded-2xl overflow-hidden" style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border-color)',
    }}>
      {header}
      {!collapsed && (
        <div className="px-5 pb-5">
          <p className="text-sm text-center py-4" style={{ color: 'var(--text-dim)' }}>
            Waiting for data... Compile and attach the updated EA to start tracking.
          </p>
        </div>
      )}
    </div>
  );

  const records = ['daily', 'weekly', 'monthly', 'all_time']
    .map(type => ({ type, ...(data[type] || {}) }))
    .filter(r => r.dd_amount !== undefined && r.dd_amount !== null && r.dd_amount !== 0);

  if (records.length === 0) return (
    <div className="rounded-2xl overflow-hidden" style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border-color)',
    }}>
      {header}
      {!collapsed && (
        <div className="px-5 pb-5">
          <p className="text-sm text-center py-4" style={{ color: 'var(--text-dim)' }}>
            No floating DD records for this account. Records appear when open trades go into drawdown.
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="rounded-2xl overflow-hidden" style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border-color)',
    }}>
      {header}

      {!collapsed && (
        <div className="px-5 pb-5">
          {/* Summary mini-stats */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            {['daily', 'weekly', 'all_time'].map(type => {
              const rec = data[type];
              if (!rec || !rec.dd_amount) return (
                <div key={type} className="rounded-xl p-3 text-center" style={{
                  background: 'var(--bg-deepest)',
                  border: '1px solid var(--border-color)',
                }}>
                  <p className="text-[10px] uppercase font-medium" style={{ color: 'var(--text-dim)' }}>{type.replace('_', ' ')} Record</p>
                  <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>No record</p>
                </div>
              );
              return (
                <div key={type} className="rounded-xl p-3 text-center group" style={{
                  background: 'linear-gradient(135deg, rgba(239,68,68,0.06), rgba(239,68,68,0.02))',
                  border: '1px solid rgba(239,68,68,0.15)',
                }}>
                  <p className="text-[10px] uppercase font-medium" style={{ color: 'var(--text-dim)' }}>{type.replace('_', ' ')} Record</p>
                  <p className="text-base font-bold mt-1 font-mono text-red-400">
                    -${Math.abs(rec.dd_amount).toFixed(2)}
                  </p>
                  <p className="text-[10px] mt-0.5 text-red-400/60">{rec.dd_percent?.toFixed(2)}%</p>
                  <SourceEABadge source={rec.source_ea} compact />
                </div>
              );
            })}
          </div>

          {/* Record list */}
          <div className="space-y-0">
            {records.map(rec => (
              <RecordRow key={rec.type} record={rec} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RecordRow({ record }) {
  const isNew = record.recorded_at && isWithinHours(record.recorded_at, 24);
  const isImproved = record.beaten_at === null && !isNew && record.is_current_record;

  return (
    <div className="flex items-center justify-between py-3 border-b last:border-0" style={{ borderColor: 'var(--border-color)' }}>
      <div className="flex items-start gap-2.5">
        <span className="text-red-400 text-sm mt-0.5">✱</span>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-medium" style={{ color: 'var(--text-primary)' }}>
              {RECORD_LABELS[record.type] || record.type}
            </span>
            {isNew && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-md" style={{
                background: 'rgba(239,68,68,0.12)',
                color: '#ef4444',
                border: '1px solid rgba(239,68,68,0.25)',
              }}>NEW</span>
            )}
          </div>
          <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-dim)' }}>
            {record.recorded_at ? formatTimestamp(record.recorded_at) : 'Unknown'} • Peak: ${record.peak_equity?.toFixed(2) || '0.00'}
          </p>
          <SourceEABadge source={record.source_ea} />
        </div>
      </div>

      <div className="text-right">
        <p className="font-bold text-[14px] font-mono text-red-400">
          -${Math.abs(record.dd_amount || 0).toFixed(2)}
        </p>
        <span className="text-[11px] px-2 py-0.5 rounded-md font-medium" style={{
          background: 'rgba(239,68,68,0.1)',
          color: '#ef4444',
        }}>
          {record.dd_percent?.toFixed(2) || '0.00'}%
        </span>
      </div>
    </div>
  );
}


function SourceEABadge({ source, compact = false }) {
  if (!source || !source.magic) return null;
  const accountText = source.account_number ? ` � #${source.account_number}` : '';
  return (
    <div
      className={`${compact ? 'mt-1 justify-center' : 'mt-1'} flex items-center gap-1 text-[10px]`}
      style={{ color: 'var(--text-secondary)' }}
      title={`Worst per-EA floating DD: ${source.ea_name || `EA_${source.magic}`}`}
    >
      <span
        className="inline-flex max-w-[180px] items-center gap-1 rounded-md px-1.5 py-0.5 font-semibold"
        style={{
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.18)',
          color: '#fca5a5',
        }}
      >
        <span className="truncate">{source.ea_name || `EA_${source.magic}`}</span>
        <span style={{ color: 'var(--text-dim)' }}>#{source.magic}</span>
      </span>
      {!compact && accountText && <span style={{ color: 'var(--text-dim)' }}>{accountText}</span>}
    </div>
  );
}
function formatTimestamp(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  return d.toLocaleString('en-US', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).replace(',', '');
}

function isWithinHours(isoStr, hours) {
  if (!isoStr) return false;
  const d = new Date(isoStr);
  const now = new Date();
  return (now - d) < hours * 3600000;
}
